import React from "react";
import useEvidence from "./useEvidence";
import { colorFor, hrefFor } from "./palette";
import { describeTrail, trailsFrom } from "./trails";
import { SOURCE_CLASS_LABEL, TRANSMISSION_LABEL } from "../network/labels";

// The evidence, written as prose rather than filled into a form.
//
// The earlier version of this panel was a definition list — "Who says so",
// "Earliest verified attestation", "Ancient or scriptural source" — which is
// accurate and reads like a tax return. Everything it said is still here; it
// says it in sentences.

function sacredLine(sacred) {
  if (sacred === "attested") return "An ancient source says it too.";
  if (sacred === "none_located") return "No ancient or scriptural source has been found for this correspondence.";
  return "Ancient and scriptural sources have not yet been checked for this correspondence.";
}

function Citation({ citation }) {
  return (
    <li className="evidence__item" data-source-class={citation.sourceClass}>
      <p className="evidence__work">
        {citation.author ? `${citation.author}, ` : ""}
        <cite>{citation.title}</cite>
        {citation.locator ? `, ${citation.locator}` : ""}
        {citation.yearDisplay ? <span className="evidence__year"> · {citation.yearDisplay}</span> : null}
      </p>
      {citation.quote ? <blockquote className="evidence__quote">{citation.quote}</blockquote> : null}
      <p className="evidence__meta">
        {SOURCE_CLASS_LABEL[citation.sourceClass] || citation.sourceClass}
        {citation.transmissionRole
          ? ` — ${TRANSMISSION_LABEL[citation.transmissionRole] || citation.transmissionRole}`
          : ""}
        {/* A locator with no quotation is a rights fact, and a reader should
            know which of the two they are looking at. */}
        {citation.restricted ? " · in copyright, so a locator and no quotation" : ""}
      </p>
      {citation.note ? <p className="evidence__note">{citation.note}</p> : null}
      {citation.url ? (
        <p><a href={citation.url} target="_blank" rel="noreferrer">Read it</a></p>
      ) : null}
    </li>
  );
}

function EdgeBody({ edge, nodesByKey, onSelectNode, schools }) {
  const from = nodesByKey.get(edge.from);
  const to = nodesByKey.get(edge.to);
  const named = edge.schools.map((slug) => schools[slug]?.name).filter(Boolean);
  const evidence = useEvidence(edge);

  return (
    <>
      <p className="panel-lede">
        {named.length > 1
          ? `${named.join(" and ")} both say so.`
          : named.length === 1
            ? `${named[0]} says so.`
            : "No school is named for this correspondence."}
        {edge.earliestDisplay ? ` The oldest source anyone has found is ${edge.earliestDisplay}.` : ""}
        {" "}{sacredLine(edge.sacred)}
      </p>

      {evidence.status === "loading" ? (
        <p className="evidence__none" data-testid="evidence-loading">Fetching the sources…</p>
      ) : null}
      {evidence.status === "failed" ? (
        <p className="evidence__none" data-testid="evidence-failed">
          The sources could not be loaded. This correspondence still has {edge.sourceCount} cited {edge.sourceCount === 1 ? "source" : "sources"}.
        </p>
      ) : null}

      {evidence.assertions.map((assertion) => (
        <section key={assertion.id} className="evidence" data-testid={`evidence-${assertion.id}`}>
          <h4 className="evidence__school">
            {assertion.school ? assertion.school.name : "Unattributed"}
            {assertion.school ? <span className="evidence__period"> · {assertion.school.period}</span> : null}
            {assertion.status !== "traditional" ? (
              <span className={`badge badge--${assertion.status}`}>{assertion.status}</span>
            ) : null}
          </h4>
          {assertion.explanation ? <p className="evidence__why">{assertion.explanation}</p> : null}
          {assertion.evidenceMode === "derived" ? (
            <p className="evidence__mode">Put together from several passages rather than stated outright.</p>
          ) : null}
          <ol className="evidence__list">
            {assertion.citations.map((citation) => (
              <Citation key={citation.id} citation={citation} />
            ))}
          </ol>
          {assertion.citations.length === 0 ? (
            <p className="evidence__none" data-testid={`no-evidence-${assertion.id}`}>
              No supporting source has been found, so this correspondence remains provisional.
            </p>
          ) : null}
        </section>
      ))}

      <p className="panel-jump">
        {[from, to].filter(Boolean).map((node) => (
          <button
            key={node.key}
            type="button"
            className="panel-jump__button"
            data-testid={`jump-${node.key}`}
            onClick={() => onSelectNode(node)}
          >
            {node.name}
          </button>
        ))}
      </p>
    </>
  );
}

// Grouped by what the relation is, strongest first inside each group, and a
// few at a time. A reader arriving at Saturn wants the metal and the day
// before the sixty-third Ptolemaic province.
const PER_GROUP = 5;

function NodeBody({ atlas, node, onSelectEdge }) {
  const [openGroups, setOpenGroups] = React.useState(() => new Set());
  const edges = (atlas.edgesFor ? atlas.edgesFor(node.key) : []).filter(Boolean);

  const groups = new Map();
  [...edges]
    .sort((a, b) => (b.weight || 0) - (a.weight || 0))
    .forEach((edge) => {
      if (!groups.has(edge.label)) groups.set(edge.label, []);
      groups.get(edge.label).push(edge);
    });

  return (
    <>
      {node.summary ? <p className="panel-lede">{node.summary}</p> : null}
      {[...groups.entries()].map(([label, all]) => {
        const open = openGroups.has(label);
        const shown = open ? all : all.slice(0, PER_GROUP);
        return (
          <section key={label} className="panel-group" data-testid={`panel-group-${label}`}>
            <h4 className="panel-group__head">
              {label}
              <span className="panel-group__count">{all.length}</span>
            </h4>
            <ul className="panel-links">
              {shown.map((edge) => {
                const otherKey = edge.from === node.key ? edge.to : edge.from;
                const other = atlas.nodesByKey.get(otherKey);
                return (
                  <li key={edge.id}>
                    <button
                      type="button"
                      className="panel-links__row"
                      data-sacred={edge.sacred}
                      data-testid={`panel-edge-${edge.id}`}
                      onClick={() => onSelectEdge(edge)}
                    >
                      <span className="panel-links__other">{other?.name}</span>
                      <span className="panel-links__when">{edge.earliestDisplay || "undated"}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {all.length > PER_GROUP ? (
              <button
                type="button"
                className="panel-group__more"
                data-testid={`panel-more-${label}`}
                onClick={() => setOpenGroups((current) => {
                  const next = new Set(current);
                  if (open) next.delete(label); else next.add(label);
                  return next;
                })}
              >
                {open ? "Show fewer" : `Show all ${all.length}`}
              </button>
            ) : null}
          </section>
        );
      })}
      <Trails atlas={atlas} node={node} />

      <p className="panel-jump">
        <a href={hrefFor(node.key)}>Everything about {node.name}</a>
      </p>
    </>
  );
}

// Chains, and a sentence saying what a chain is not.
function Trails({ atlas, node }) {
  const trails = trailsFrom(node.key, {
    adjacency: atlas.adjacency,
    edgesById: atlas.edgesById,
    nodesByKey: atlas.nodesByKey
  });
  if (trails.length === 0) return null;

  return (
    <section className="panel-trails" data-testid="panel-trails">
      <h4 className="panel-group__head">Where this leads</h4>
      <p className="panel-trails__caveat">
        Routes through the web, not correspondences of their own. Every step is
        sourced; the joining of them is this map's, and nobody's else.
      </p>
      <ul className="panel-trails__list">
        {trails.map((trail) => (
          <li key={trail.end.key}>
            <button
              type="button"
              className="panel-trails__route"
              data-testid={`trail-${trail.end.key}`}
              title={describeTrail(node.name, trail, atlas.nodesByKey)}
              onClick={() => {
                trail.steps.forEach((step) => atlas.open(step.from));
                atlas.open(trail.end.key);
              }}
            >
              {[node.name, ...trail.steps.map((step) => atlas.nodesByKey.get(step.to)?.name)]
                .filter(Boolean)
                .map((name, index) => (
                  <React.Fragment key={`${name}-${index}`}>
                    {index > 0 ? <span className="panel-trails__arrow" aria-hidden="true">→</span> : null}
                    <span>{name}</span>
                  </React.Fragment>
                ))}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function EvidencePanel({ atlas, onClose, onSelectEdge, onSelectNode, schools, selected }) {
  if (!selected) {
    return (
      <aside className="atlas-panel atlas-panel--empty" data-testid="atlas-panel">
        <p>
          Click a circle to open what it connects to. Click a line to see who
          said it, when, and whether anything ancient backs them up.
        </p>
        <p className="atlas-panel__legend">
          A <strong>solid</strong> line has an ancient source behind it.
          A <strong>dashed</strong> one has been searched for and none was found.
        </p>
      </aside>
    );
  }

  const isEdge = selected.type === "edge";
  const node = isEdge ? null : atlas.nodesByKey.get(selected.key);
  const edge = isEdge ? atlas.edgesById.get(selected.id) : null;
  const from = isEdge ? atlas.nodesByKey.get(edge?.from) : null;
  const to = isEdge ? atlas.nodesByKey.get(edge?.to) : null;

  return (
    <aside className="atlas-panel" data-testid="atlas-panel" aria-live="polite">
      <div className="atlas-panel__head">
        <p className="atlas-panel__kicker">
          {isEdge ? edge?.label : node?.kind}
        </p>
        <h3 className="atlas-panel__title">
          {isEdge ? (
            <>
              <span style={{ color: colorFor(from?.group) }}>{from?.name}</span>
              {" → "}
              <span style={{ color: colorFor(to?.group) }}>{to?.name}</span>
            </>
          ) : node?.name}
        </h3>
        <button type="button" className="atlas-panel__close" onClick={onClose} aria-label="Close this panel">×</button>
      </div>

      <div className="atlas-panel__body">
        {isEdge && edge ? (
          <EdgeBody edge={edge} nodesByKey={atlas.nodesByKey} onSelectNode={onSelectNode} schools={schools || {}} />
        ) : null}
        {!isEdge && node ? (
          <NodeBody atlas={atlas} node={node} onSelectEdge={onSelectEdge} />
        ) : null}
      </div>
    </aside>
  );
}
